/**
 * A colour name from a supplier export, turned into a swatch.
 *
 * The catalogue loader wrote `colors: ['Black', 'Bone']` — names with no
 * colour. `products.colors` is jsonb, so it stored them, and the admin
 * catalogue screen read `colors[0].hex` and died. The client's own 1,896
 * variant export would have done that on the first row, on go-live day. R-192.
 *
 * There is no hex in the export, so it is derived from the name. Two rules:
 *
 *   A name this recognises gets its real colour.
 *   A name it does not gets a neutral swatch AND IS PRINTED, because a wrong
 *   colour shown confidently is worse than a grey one somebody fixes. The
 *   loader lists every unmapped name at the end so it can be set on the
 *   catalogue screen.
 */

/** Apparel colours, as blank suppliers name them. Lower case, no punctuation. */
const SWATCHES: Record<string, string> = {
  black: '#16191C',
  white: '#FFFFFF',
  bone: '#E5E1D9',
  natural: '#EDE6D6',
  cream: '#F3EBDC',
  sand: '#D9C9AE',
  stone: '#C8C1B3',
  grey: '#8A8378',
  gray: '#8A8378',
  'light grey': '#C2C7CD',
  'dark grey': '#3E4348',
  charcoal: '#2E333A',
  navy: '#1B2A44',
  'royal blue': '#1E4FA8',
  blue: '#1E4FA8',
  'sky blue': '#7EAAF0',
  teal: '#12707A',
  green: '#1F7A4C',
  'bottle green': '#12402C',
  olive: '#5A5A34',
  khaki: '#8F8259',
  red: '#A8351F',
  maroon: '#5E1F1A',
  burgundy: '#5E1F2E',
  orange: '#D2641F',
  'hi-vis orange': '#FF6A00',
  'hi-vis yellow': '#E9E200',
  yellow: '#FFCE1F',
  gold: '#C8A02A',
  pink: '#E39BB0',
  purple: '#5B3D7A',
  brown: '#5C4433',
  tan: '#B08A5F',
};

/** What an unrecognised colour gets: neutral, obviously placeholder, not wrong. */
export const UNKNOWN_SWATCH = '#C8C1B3';

export interface Swatch {
  name: string;
  hex: string;
}

/**
 * The swatch for a colour name, and whether it was recognised.
 *
 * Matching ignores case, punctuation and extra spaces, because "Hi Vis Yellow",
 * "hi-vis yellow" and "HI-VIS  YELLOW" are one colour in three exports.
 */
/** Hyphens, slashes, underscores and runs of space all mean one space. */
const flatten = (s: string) =>
  s.trim().toLowerCase().replace(/[-_/]+/g, ' ').replace(/\s+/g, ' ');

/**
 * Longest key first.
 *
 * "hi vis yellow" ends with " yellow", so a pass in declaration order matched
 * plain yellow and returned #FFCE1F for a safety garment. The most specific
 * name has to win, and the most specific name is the longest one.
 */
const KEYS_BY_LENGTH = Object.keys(SWATCHES)
  .map((k) => [flatten(k), SWATCHES[k]!] as const)
  .sort((a, b) => b[0].length - a[0].length);

export function swatchFor(name: string): { swatch: Swatch; known: boolean } {
  const key = flatten(name);
  const label = name.trim();

  for (const [word, hex] of KEYS_BY_LENGTH) {
    // Exact, or the base colour under a texture word: "Black Marle" and
    // "Heather Grey" are one colour with a finish, and a hundred entries for
    // the finishes would be a list nobody maintains.
    if (key === word || key.startsWith(`${word} `) || key.endsWith(` ${word}`)) {
      return { swatch: { name: label, hex }, known: true };
    }
  }

  return { swatch: { name: label, hex: UNKNOWN_SWATCH }, known: false };
}
